import { z } from 'zod';
import { LOCALES, ORGANIZATION_ROLES, PLATFORM_ROLES } from '../constants.js';
import { PERMISSIONS } from '../permissions.js';
import { nameSchema } from './common.js';

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(200)
  .refine((v) => /[a-zA-Z]/.test(v) && /[0-9]/.test(v), {
    message: 'Password must contain letters and numbers',
  });

export const emailSchema = z.string().trim().toLowerCase().email().max(254);

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: nameSchema,
  organizationName: nameSchema,
  /** Optional IANA timezone captured from the browser to seed the first location. */
  timezone: z.string().max(64).optional(),
  /** The language the sign-up page was in; the account starts in it. */
  locale: z.enum(LOCALES).optional(),
});
export type SignupInput = z.infer<typeof signupSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  /** When false the refresh cookie is a session cookie. */
  rememberMe: z.boolean().default(true),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const setLocaleSchema = z.object({ locale: z.enum(LOCALES) });

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z.object({
  token: z.string().min(10).max(200),
  password: passwordSchema,
});

export const verifyEmailSchema = z.object({ token: z.string().min(10).max(200) });

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});

export const acceptInviteSchema = z.object({
  token: z.string().min(10).max(200),
  /** Required when the invited email has no account yet. */
  name: nameSchema.optional(),
  password: passwordSchema.optional(),
});

export const inviteUserSchema = z.object({
  email: emailSchema,
  role: z.enum(ORGANIZATION_ROLES),
  name: nameSchema.optional(),
});

export const updateMemberSchema = z.object({
  role: z.enum(ORGANIZATION_ROLES).optional(),
  customPermissions: z.array(z.enum(PERMISSIONS)).max(50).optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  emailVerified: z.boolean(),
  platformRole: z.enum(PLATFORM_ROLES),
  avatarColor: z.string().nullable(),
  /** When the dashboard tour was finished or skipped; null shows it on the next sign-in. */
  tourCompletedAt: z.string().nullable().optional(),
  /** The dashboard's language for this person. */
  locale: z.enum(LOCALES).optional(),
  /** Whether they chose it themselves (otherwise it follows their Oicas account). */
  localeChosen: z.boolean().optional(),
  createdAt: z.string(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const membershipSchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  organizationSlug: z.string(),
  role: z.enum(ORGANIZATION_ROLES),
  status: z.string(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const sessionResponseSchema = z.object({
  user: publicUserSchema,
  memberships: z.array(membershipSchema),
  /** The organization the request context resolved to, if any. */
  currentOrganizationId: z.string().nullable(),
  permissions: z.array(z.string()),
  /** Set while a platform administrator is signed in as this user. */
  impersonator: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable().optional(),
  /** Present for non-cookie clients. */
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  accessTokenExpiresAt: z.string().optional(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const refreshSchema = z.object({ refreshToken: z.string().optional() });
